import type { Role } from '@/domain/types';
import { LastCallDB } from './db';

/**
 * Two separate sets of records on a device:
 *  reds      Red's real counts.
 *  practice  Made-up numbers for training and showing the app. Never mixed with the real ones.
 */
export type DataMode = 'reds' | 'practice';

export interface Actor {
  userId: string;
  role: Role;
  name: string;
}

export interface Runtime {
  db: LastCallDB;
  mode: DataMode;
  actor: Actor;
}

let current: Runtime | null = null;

export function rt(): Runtime {
  if (!current) throw new Error('Storage is not open yet');
  return current;
}

export function hasRuntime(): boolean {
  return current !== null;
}

export function setRuntime(r: Runtime): void {
  current = r;
}

export function updateRuntime(patch: Partial<Runtime>): void {
  if (!current) throw new Error('Storage is not open yet');
  current = { ...current, ...patch };
}

/**
 * On the dev server only, a tab opened with ?device=2 keeps its own storage, so one browser
 * can act as two devices while trying out sharing.
 */
export function devDevice(): string | null {
  if (!import.meta.env?.DEV || typeof window === 'undefined') return null;
  try {
    const asked = new URLSearchParams(window.location.search).get('device');
    if (asked && /^[a-z0-9]{1,8}$/i.test(asked)) sessionStorage.setItem('lastcall.devDevice', asked);
    return sessionStorage.getItem('lastcall.devDevice');
  } catch {
    return null;
  }
}

export function dbNameFor(mode: DataMode): string {
  const dev = devDevice();
  return `${mode === 'reds' ? 'lastcall-reds' : 'lastcall-practice'}${dev ? `-device-${dev}` : ''}`;
}
